
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import Stepper from "../../components/Stepper";

const FUNCTION_URL =
  "https://southamerica-east1-feirateste-279c6.cloudfunctions.net/cadastrarVisitante";

const RECAPTCHA_SITE_KEY = "6LcK-GssAAAAAKSe6hUW_OGIFlpJbUo83vPIKYeZ";

export default function CadastroVisitante() {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cidade, setCidade] = useState("");

  const [interesseComercial, setInteresseComercial] = useState("");
  const [cnpj, setCnpj] = useState("");

  const [sucesso, setSucesso] = useState(false);
  const [erroTermos, setErroTermos] = useState({});
  const [loading, setLoading] = useState(false);

  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [recaptchaRetry, setRecaptchaRetry] = useState(0);

  const { id: eventoId } = useParams();

  const navigate = useNavigate();
  const auth = getAuth();

  // ===== Verificação se usuário é administrador =====

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        setIsAdmin(!!idTokenResult.claims.admin);
      } catch (error) {
        console.error("Erro ao verificar administrador:", error);
        setIsAdmin(false);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  // ===== Carregar reCAPTCHA invisível =====

  useEffect(() => {
    if (isAdmin) return;

    let cancelled = false;
    let timeoutId;

    const container = document.getElementById("recaptcha-container");

    if (!container) {
      setRecaptchaReady(false);
      setRecaptchaError(true);
      return;
    }

    setRecaptchaReady(false);
    setRecaptchaError(false);
    setRecaptchaWidgetId(null);

    container.innerHTML = "";

    let script = document.getElementById("recaptcha-script");

    if (recaptchaRetry > 0 && script) {
      script.remove();
      script = null;
    }

    const fail = () => {
      if (cancelled) return;

      clearTimeout(timeoutId);
      setRecaptchaReady(false);
      setRecaptchaError(true);
    };

    const renderWidget = () => {
      if (cancelled) return;

      if (
        !window.grecaptcha ||
        typeof window.grecaptcha.render !== "function"
      ) {
        fail();
        return;
      }

      try {
        const id = window.grecaptcha.render(container, {
          sitekey: RECAPTCHA_SITE_KEY,
          size: "invisible",
          badge: "bottomright",
        });

        if (cancelled) return;

        clearTimeout(timeoutId);
        setRecaptchaWidgetId(id);
        setRecaptchaReady(true);
        setRecaptchaError(false);
      } catch (error) {
        console.error("Erro ao renderizar reCAPTCHA:", error);
        fail();
      }
    };

    const initialize = () => {
      if (cancelled) return;

      if (
        !window.grecaptcha ||
        typeof window.grecaptcha.ready !== "function"
      ) {
        fail();
        return;
      }

      window.grecaptcha.ready(renderWidget);
    };

    timeoutId = setTimeout(fail, 15000);

    if (!script) {
      script = document.createElement("script");
      script.id = "recaptcha-script";
      script.src =
        "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;

      script.onload = initialize;
      script.onerror = fail;

      document.head.appendChild(script);
    } else if (
      window.grecaptcha &&
      typeof window.grecaptcha.render === "function"
    ) {
      initialize();
    } else {
      script.addEventListener("load", initialize, { once: true });
      script.addEventListener("error", fail, { once: true });
    }

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isAdmin, recaptchaRetry]);

  // ===== Funções do reCAPTCHA =====

  const executeRecaptcha = async () => {
    if (!recaptchaReady || recaptchaWidgetId === null) {
      throw new Error(
        "A verificação de segurança não está pronta. Tente novamente."
      );
    }

    if (
      !window.grecaptcha ||
      typeof window.grecaptcha.execute !== "function"
    ) {
      throw new Error("reCAPTCHA não inicializado");
    }

    return await window.grecaptcha.execute(recaptchaWidgetId);
  };

  const resetRecaptcha = () => {
    if (window.grecaptcha && recaptchaWidgetId !== null) {
      window.grecaptcha.reset(recaptchaWidgetId);
    }
  };

  // ===== Formatação e validações =====

  const formatCpf = (valor) =>
    valor
      .replace(/\D/g, "")
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

  const formatNome = (valor) =>
    valor
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .map(
        (parte) =>
          parte.charAt(0).toUpperCase() + parte.slice(1)
      )
      .join(" ");

  const validarCPF = (cpf) => {
    cpf = cpf.replace(/\D/g, "");

    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
      return false;
    }

    let soma = 0;

    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf[i], 10) * (10 - i);
    }

    let dig1 = (soma * 10) % 11;
    if (dig1 === 10) dig1 = 0;

    if (dig1 !== parseInt(cpf[9], 10)) {
      return false;
    }

    soma = 0;

    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf[i], 10) * (11 - i);
    }

    let dig2 = (soma * 10) % 11;
    if (dig2 === 10) dig2 = 0;

    if (dig2 !== parseInt(cpf[10], 10)) {
      return false;
    }

    return true;
  };

  const validarNomeCompleto = (nome) => {
    const partes = nome.trim().split(/\s+/);
    return partes.length >= 2 && partes[1].length > 1;
  };

  const formatTelefone = (valor) => {
    const numeros = valor.replace(/\D/g, "").slice(0, 11);

    if (numeros.length === 0) return "";

    if (numeros.length <= 2) {
      return `(${numeros}`;
    }

    const ddd = numeros.slice(0, 2);
    const restante = numeros.slice(2);

    if (restante.length <= 4) {
      return `(${ddd}) ${restante}`;
    }

    if (restante.length <= 8) {
      return `(${ddd}) ${restante.slice(0, 4)}-${restante.slice(4)}`;
    }

    return `(${ddd}) ${restante.slice(0, 5)}-${restante.slice(5)}`;
  };

  const formatCnpj = (valor) => {
    return valor
      .replace(/\D/g, "")
      .slice(0, 14)
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  };

  const validarCNPJ = (valor) => {
    const numeros = valor.replace(/\D/g, "");

    if (numeros.length !== 14 || /^(\d)\1+$/.test(numeros)) {
      return false;
    }

    const calcularDigito = (base) => {
      const pesos = [2, 3, 4, 5, 6, 7, 8, 9];
      let soma = 0;

      for (let i = base.length - 1; i >= 0; i--) {
        const peso = pesos[(base.length - 1 - i) % pesos.length];
        soma += Number(base[i]) * peso;
      }

      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    const primeiroDigito = calcularDigito(numeros.slice(0, 12));

    const segundoDigito = calcularDigito(
      numeros.slice(0, 12) + primeiroDigito
    );

    return (
      numeros[12] === String(primeiroDigito) &&
      numeros[13] === String(segundoDigito)
    );
  };

  // ===== Envio para Cloud Function =====

  const enviarEmail = async (
    email,
    nome,
    cpf,
    telefone,
    cidade,
    interesseComercial,
    cnpj,
  ) => {
    if (!eventoId) {
      throw new Error("Evento não selecionado");
    }

    let token = null;

    if (!isAdmin) {
      token = await executeRecaptcha();
    }

    const idToken = auth.currentUser
      ? await auth.currentUser.getIdToken()
      : null;

    const res = await fetch(FUNCTION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        nome,
        cpf,
        telefone,
        cidade,

        // Mantidos para compatibilidade com a função atual.
        aceitaEmail: false,
        aceitaTermos: false,

        interesseComercial,
        cnpj: cnpj.replace(/\D/g, "") || null,

        recaptchaToken: token,
        idToken,
        eventoId,
      }),
    });

    let data = {};

    try {
      data = await res.json();
    } catch (error) {
      throw new Error("Resposta inválida do servidor");
    }

    if (!res.ok) {
      throw new Error(data.error || "Falha na Cloud Function");
    }

    return data;
  };

  // ===== Envio do formulário =====

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (
      !isAdmin &&
      (!recaptchaReady || recaptchaWidgetId === null || recaptchaError)
    ) {
      return;
    }

    const telefoneLimpo = telefone.replace(/\D/g, "");
    const cnpjLimpo = cnpj.replace(/\D/g, "");

    const erros = {};

    const cidadeLimpa = cidade.trim();

    if (!cidadeLimpa) {
    erros.cidade = "Informe sua cidade.";
    } else if (cidadeLimpa.length > 20) {
    erros.cidade = "A cidade deve ter no máximo 20 caracteres.";
    }

    if (!cpf || !validarCPF(cpf)) {
      erros.cpf = "CPF inválido.";
    }

    if (!validarNomeCompleto(nome)) {
      erros.nome = "Digite seu nome completo.";
    }

    if (telefoneLimpo.length < 10 || telefoneLimpo.length > 11) {
      erros.telefone = "Digite um telefone válido com DDD.";
    }

    if (!interesseComercial) {
      erros.interesseComercial = "Selecione uma opção.";
    }

    if (
      interesseComercial === "sim" &&
      cnpjLimpo.length > 0 &&
      !validarCNPJ(cnpj)
    ) {
      erros.cnpj = "CNPJ inválido.";
    }

    if (Object.keys(erros).length > 0) {
      setErroTermos(erros);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);

      return;
    }

    setErroTermos({});
    setLoading(true);

    try {
      const nomeFormatado = formatNome(nome);

      const resposta = await enviarEmail(
        email,
        nomeFormatado,
        cpf.replace(/\D/g, ""),
        telefoneLimpo,
        cidadeLimpa,
        interesseComercial,
        cnpj
      );

      if (!resposta?.credencial?.codigo) {
        throw new Error(
          "O cadastro não retornou o código da credencial."
        );
      }

      setSucesso(true);

      setNome("");
      setEmail("");
      setCpf("");
      setTelefone("");
      setInteresseComercial("");
      setCnpj("");
      setCidade("");

      resetRecaptcha();
    } catch (err) {
      console.error("Erro ao cadastrar visitante:", err);

      if (err.message.includes("CPF já cadastrado")) {
        setErroTermos((prev) => ({
          ...prev,
          cpf: err.message,
        }));
      } else {
        alert(err.message);
      }

      resetRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  // ===== JSX =====

  return (
    <div
      className="page-bg"
      style={{
        backgroundImage: 'url("/imagens/textura-hexagono-azul.png")',
      }}
    >
      <div className="termo-wrapper">
        <div className="page">
          <h1>Credenciamento</h1>

          <div className="card">
            <form onSubmit={handleSubmit}>
              {!isAdmin && !recaptchaReady && (
                <div
                  role="status"
                  style={{
                    padding: "12px",
                    marginBottom: "15px",
                    borderRadius: "8px",
                    background: "#1f2430",
                    color: "#ffffff",
                    textAlign: "center",
                  }}
                >
                  {recaptchaError ? (
                    <>
                      <p>
                        Não foi possível carregar a verificação de segurança.
                        Verifique sua conexão e tente novamente.
                      </p>

                      <button
                        type="button"
                        onClick={() => {
                          setRecaptchaReady(false);
                          setRecaptchaError(false);
                          setRecaptchaWidgetId(null);
                          setRecaptchaRetry((prev) => prev + 1);
                        }}
                      >
                        Tentar novamente
                      </button>
                    </>
                  ) : (
                    <p>
                      Aguarde o carregamento da verificação de segurança...
                    </p>
                  )}
                </div>
              )}

              <fieldset
                disabled={loading || (!isAdmin && !recaptchaReady)}
                style={{
                  border: "none",
                  padding: 0,
                  margin: 0,
                  minWidth: 0,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                
                {/* ===== Interesse comercial ===== */}

              <div className="consentimento">
                <label>
                  Qual é o seu principal interesse ao visitar o evento?
                  <span style={{ color: "#f59e0b" }}> *</span>
                </label>

                <label className="checkbox interesse-opcao">
                  <input
                    type="radio"
                    name="interesseComercial"
                    value="sim"
                    checked={interesseComercial === "sim"}
                    onChange={() => {
                      setInteresseComercial("sim");

                      setErroTermos((prev) => ({
                        ...prev,
                        interesseComercial: undefined,
                      }));
                    }}
                    required
                    disabled={loading}
                  />

                  <span className="interesse-texto">
                    <strong>Negócios e conexões profissionais</strong>
                    <small>
                      Visitar estandes, conhecer fornecedores, descobrir produtos e gerar
                      oportunidades de negócios.
                    </small>
                  </span>
                </label>

                <label className="checkbox interesse-opcao">
                  <input
                    type="radio"
                    name="interesseComercial"
                    value="nao"
                    checked={interesseComercial === "nao"}
                    onChange={() => {
                      setInteresseComercial("nao");
                      setCnpj("");

                      setErroTermos((prev) => ({
                        ...prev,
                        interesseComercial: undefined,
                        cnpj: undefined,
                      }));
                    }}
                    required
                    disabled={loading}
                  />

                  <span className="interesse-texto">
                    <strong>Gastronomia e experiências</strong>
                    <small>
                      Experimentar produtos, aproveitar o Festival Gastronômico e passear
                      pela feira.
                    </small>
                  </span>
                </label>

                {erroTermos.interesseComercial && (
                  <div
                    style={{
                      color: "red",
                      fontSize: "0.85rem",
                      marginTop: "4px",
                    }}
                  >
                    {erroTermos.interesseComercial}
                  </div>
                )}

                {interesseComercial === "sim" && (
                  <>
                    <label>CNPJ (opcional)</label>

                    <input
                      type="text"
                      placeholder="00.000.000/0000-00"
                      value={cnpj}
                      onChange={(e) => {
                        setCnpj(formatCnpj(e.target.value));

                        if (erroTermos.cnpj) {
                          setErroTermos((prev) => ({
                            ...prev,
                            cnpj: undefined,
                          }));
                        }
                      }}
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                        boxSizing: "border-box",
                        display: "block",
                      }}
                      disabled={loading}
                    />

                    {erroTermos.cnpj && (
                      <div
                        style={{
                          color: "red",
                          fontSize: "0.85rem",
                          marginTop: "4px",
                        }}
                      >
                        {erroTermos.cnpj}
                      </div>
                    )}
                  </>
                )}
</div>
                <label>Nome completo
                    <span style={{ color: "#f59e0b" }}> *</span>
                </label>

                <input
                  type="text"
                  value={nome}
                  onChange={(e) => {
                    setNome(e.target.value);

                    if (erroTermos.nome) {
                      setErroTermos((prev) => ({
                        ...prev,
                        nome: undefined,
                      }));
                    }
                  }}
                  required
                  disabled={loading}
                />

                {erroTermos.nome && (
                  <div
                    style={{
                      color: "red",
                      fontSize: "0.85rem",
                      marginTop: "4px",
                    }}
                  >
                    {erroTermos.nome}
                  </div>
                )}

                <label>Email
                      <span style={{ color: "#f59e0b" }}> *</span>
                </label>

                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />

                <label>CPF
                      <span style={{ color: "#f59e0b" }}> *</span>
                </label>

                <input
                  type="text"
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={(e) => {
                    setCpf(formatCpf(e.target.value));

                    if (erroTermos.cpf) {
                      setErroTermos((prev) => ({
                        ...prev,
                        cpf: undefined,
                      }));
                    }
                  }}
                  required
                  disabled={loading}
                />

                {erroTermos.cpf && (
                  <div
                    style={{
                      color: "red",
                      fontSize: "0.85rem",
                      marginTop: "4px",
                    }}
                  >
                    {erroTermos.cpf}
                  </div>
                )}

                <label>Telefone
                      <span style={{ color: "#f59e0b" }}> *</span>
                </label>

                <input
                  type="tel"
                  placeholder="(41) 99999-9999"
                  value={telefone}
                  onChange={(e) => {
                    setTelefone(formatTelefone(e.target.value));

                    if (erroTermos.telefone) {
                      setErroTermos((prev) => ({
                        ...prev,
                        telefone: undefined,
                      }));
                    }
                  }}
                  required
                  disabled={loading}
                />

                {erroTermos.telefone && (
                  <div
                    style={{
                      color: "red",
                      fontSize: "0.85rem",
                      marginTop: "4px",
                    }}
                  >
                    {erroTermos.telefone}
                  </div>
                )}

                <label>Cidade
                      <span style={{ color: "#f59e0b" }}> *</span>
                </label>

                <input
                type="text"
                placeholder="Ex.: Curitiba - PR"
                value={cidade}
                onChange={(e) => {
                setCidade(e.target.value);

                if (erroTermos.cidade) {
                    setErroTermos((prev) => ({
                    ...prev,
                    cidade: undefined,
                    }));
                }
                }}
                maxLength={20}
                required
                disabled={loading}
                />
                {erroTermos.cidade && (
                <div style={{ color: "red", fontSize: "0.85rem", marginTop: "4px" }}>
                    {erroTermos.cidade}
                </div>
                )}


                {/* ===== Aviso de privacidade ===== */}

                <div
                  style={{
                    background: "#1f2430",
                    borderRadius: "8px",
                    padding: "12px 14px",
                    marginTop: "15px",
                    fontSize: "12px",
                    color: "#9ca3af",
                    lineHeight: "1.5",
                  }}
                >
                  Seus dados serão tratados para fins de credenciamento,
                  controle de acesso ao evento e, quando aplicável,
                  registro de interesse comercial, conforme estabelece
                  a Lei Geral de Proteção de Dados Pessoais (LGPD) -
                  Lei nº 13.709/2018.
                </div>

                <button
                  type="submit"
                  disabled={loading || (!isAdmin && !recaptchaReady)}
                >
                  {loading ? "Aguarde..." : "Cadastrar"}
                </button>
              </fieldset>

              {!isAdmin && (
                <div
                  id="recaptcha-container"
                  style={{
                    position: "fixed",
                    bottom: "10px",
                    right: "10px",
                    zIndex: 1000,
                  }}
                />
              )}
            </form>
          </div>
        </div>

        <Stepper etapaAtual={3} />
      </div>

      {/* ===== Modal de sucesso ===== */}

      {sucesso && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Credenciamento concluído</h2>

            <p>
              Seu credenciamento foi realizado com sucesso.
              Agora basta se identificar na recepção do evento.
            </p>

            <p>Desejamos um excelente evento!</p>

            <button
              onClick={() => {
                const destino = isAdmin
                  ? "/consulta-cadastro"
                  : "/eventos-ativos";

                navigate(destino);
              }}
            >
              {isAdmin ? "Voltar ao cadastro" : "Voltar aos eventos"}
            </button>
          </div>
        </div>
      )}

      {/* ===== Tela de carregamento ===== */}

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Aguarde um instante...</p>
        </div>
      )}
    </div>
  );
}
